import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Clock, ArrowLeft, Loader2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords do not match."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      toast.success(data.message || "Password updated.");
      navigate("/login");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0D0C] grid-bg flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-9 h-9 rounded-lg bg-emerald-500 flex items-center justify-center"><Clock className="w-5 h-5 text-[#062217]" /></div>
          <span className="font-display text-2xl font-extrabold tracking-tight">QwikLogs</span>
        </Link>
        <div className="card-emerald p-8 animate-fade-up">
          <h1 className="font-display text-2xl font-bold">Set a new password</h1>
          {!token ? (
            <p className="text-sm text-red-300 mt-2">This reset link is missing its token. Please use the link from your email.</p>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="pw">New password</Label>
                <Input id="pw" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="reset-password-input" />
              </div>
              <div>
                <Label htmlFor="cf">Confirm password</Label>
                <Input id="cf" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="reset-confirm-input" />
              </div>
              <Button type="submit" disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="reset-submit-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
              </Button>
            </form>
          )}
        </div>
        <Link to="/login" className="flex items-center gap-1 justify-center text-sm text-muted-foreground hover:text-foreground mt-6">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
      </div>
    </div>
  );
}
